import React from 'react';
import { useCurrency } from '../../../context/CurrencyContext';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Callout } from '../../../components/ui/Callout';
import { Icon } from '../../../components/ui/Icon';
import { Field } from '../../../components/ui/Field';
import { Select } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Divider } from '../../../components/ui/Divider';
import EnhancedItemsList from '../../../components/calculator/EnhancedItemsList';
import { ShippingTerms } from '../../../components/calculator/components';
import { ShipStepNavigation } from './ShipStepNavigation';
import { useShippingWizard, ShippingSteps } from '../hooks/useShippingWizard';
import { useShippingHandlers } from '../hooks/useShippingHandlers';
import { SHIPMENT_PURPOSES } from '../constants/shippingOptions';
import { countries } from '../constants/countries';

interface Props {
  wizard: ReturnType<typeof useShippingWizard>;
  handlers: ReturnType<typeof useShippingHandlers>;
}

export const ShipStepDetails: React.FC<Props> = ({ wizard: w, handlers: h }) => {
  const { currency } = useCurrency();
  const currencySymbols: Record<string, string> = { USD: '$', ILS: '₪', EUR: '€' };
  const symbol = currencySymbols[currency] ?? currency;

  const {
    parcels,
    setParcels,
    parcelDetails,
    shipmentPurpose,
    setShipmentPurpose,
    stepValidation,
    locationState,
    newlyAddedItemId,
    setNewlyAddedItemId,
    validateParcelItems,
    goToNextStep,
  } = w;

  if (parcelDetails.packageType !== 'package') {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <div style={{ fontWeight: 500, fontSize: 15, marginBottom: 16 }}>
          Package contents not required for documents
        </div>
        <Button variant="blue" size="lg" onClick={goToNextStep}>
          <span>Continue to Summary</span>
          <Icon name="arrowRight" size={14} />
        </Button>
      </div>
    );
  }

  const isInternational = locationState?.shipmentType === 'international';
  const hasItemsInAnyParcel = parcels.some((p) => p.items && p.items.length > 0);

  // Customs invoice summary data
  const totalItemsCount = parcels.reduce((t, p) => t + (p.items?.length || 0), 0);
  const totalQuantity = parcels.reduce(
    (t, p) => t + (p.items?.reduce((pt, item) => pt + item.quantity, 0) || 0),
    0
  );
  const totalWeight = parcels.reduce(
    (t, p) => t + (p.items?.reduce((pt, item) => pt + item.weight * item.quantity, 0) || 0),
    0
  );
  const totalValue = parcels.reduce(
    (t, p) => t + (p.items?.reduce((pt, item) => pt + item.price * item.quantity, 0) || 0),
    0
  );
  const completeItems = parcels.reduce(
    (t, p) =>
      t +
      (p.items?.filter(
        (item) =>
          item.description?.length >= 10 &&
          item.madeIn?.trim() !== '' &&
          item.quantity > 0 &&
          item.weight > 0 &&
          item.price >= 0
      ).length || 0),
    0
  );
  const completionRate = totalItemsCount > 0 ? (completeItems / totalItemsCount) * 100 : 0;

  return (
    <div>
      <Card pad style={{ padding: 28 }}>
        <h2 style={{ fontSize: 20, marginBottom: 4 }}>Package Contents</h2>
        <p className="muted" style={{ fontSize: 13, marginBottom: 20 }}>
          Add detailed information about the items in your parcels. Each item description must be at
          least 10 characters long.
        </p>

        {/* Shipment Purpose */}
        <div
          style={{
            marginBottom: 24,
            padding: 16,
            borderRadius: 8,
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Shipment Purpose</div>
          <Field label="Purpose of shipment">
            <Select
              value={shipmentPurpose}
              onChange={(e) => setShipmentPurpose(e.target.value)}
            >
              {SHIPMENT_PURPOSES.map((purpose) => (
                <option key={purpose.value} value={purpose.value}>
                  {purpose.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {/* Per-parcel items management */}
        {parcels.map((parcel, parcelIndex) => {
          const totalItemWeight =
            parcel.items?.reduce((total, item) => total + item.weight * item.quantity, 0) || 0;
          const chargeableWeight = Math.max(
            typeof parcel.physicalWeight === 'number'
              ? parcel.physicalWeight
              : (parcel.weight || 0) * (parcel.quantity || 1),
            typeof parcel.dimensionalWeight === 'number' ? parcel.dimensionalWeight : 0
          );
          const weightExceeded = totalItemWeight > chargeableWeight;
          const hasValidItems = parcel.items && parcel.items.length > 0 && validateParcelItems(parcel);

          return (
            <div key={parcel.id} style={{ marginBottom: 20 }}>
              <div
                style={{
                  border: hasValidItems ? '2px solid var(--ok)' : '1px solid var(--border)',
                  borderRadius: 8,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    background: 'var(--surface-2)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>Parcel {parcelIndex + 1}</span>
                    {hasValidItems ? (
                      <Badge tone="ok">Complete</Badge>
                    ) : (
                      <Badge tone="warn">Incomplete</Badge>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
                    <span className="muted">Max: {chargeableWeight.toFixed(2)} kg</span>
                    <span
                      style={{
                        color: weightExceeded ? 'var(--err)' : 'var(--ink-2)',
                        fontWeight: weightExceeded ? 600 : 400,
                      }}
                    >
                      Used: {totalItemWeight.toFixed(2)} kg
                    </span>
                  </div>
                </div>

                <div style={{ padding: 16 }}>
                  {weightExceeded && (
                    <div style={{ marginBottom: 12 }}>
                      <Callout tone="warn">
                        <Icon name="alertCircle" size={14} />
                        <span style={{ fontSize: 13 }}>
                          Weight exceeded! Total item weight ({totalItemWeight.toFixed(2)} kg)
                          exceeds parcel weight ({parcel.weight} kg).
                        </span>
                      </Callout>
                    </div>
                  )}

                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>
                    Items in this parcel:
                  </div>

                  {!parcel.items || parcel.items.length === 0 ? (
                    <div style={{ marginBottom: 12 }}>
                      <Callout tone="info">
                        <Icon name="info" size={14} />
                        <span style={{ fontSize: 13 }}>
                          No items added yet. Click "Add Item" to start adding package contents.
                        </span>
                      </Callout>
                    </div>
                  ) : (
                    <EnhancedItemsList
                      items={parcel.items}
                      parcelIndex={parcelIndex}
                      parcels={parcels}
                      setParcels={setParcels}
                      countries={countries}
                      theme={{} as any}
                      initialEditingId={newlyAddedItemId}
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      const newItem = {
                        id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                        description: '',
                        quantity: 1,
                        weight: 0.1,
                        price: 0,
                        madeIn: '',
                        hsCode: '',
                      };
                      const updatedParcels = [...parcels];
                      const updatedItems = [...(parcel.items || []), newItem];
                      updatedParcels[parcelIndex] = { ...parcel, items: updatedItems };
                      setParcels(updatedParcels);
                      setNewlyAddedItemId(newItem.id);
                    }}
                    style={{
                      width: '100%',
                      marginTop: 10,
                      padding: '10px 16px',
                      borderRadius: 8,
                      border: '2px dashed var(--blue)',
                      background: 'transparent',
                      color: 'var(--blue)',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                    }}
                  >
                    <Icon name="plus" size={14} />
                    Add Item to Parcel {parcelIndex + 1}
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {/* VAT / Customs Terms */}
        <div style={{ marginTop: 20 }}>
          <ShippingTerms
            vatType={w.vatType}
            vatNumber={w.vatNumber}
            onVatTypeChange={h.handleVatTypeChange}
            onVatNumberChange={h.handleVatNumberChange}
          />
        </div>

        {/* Customs Invoice Preview for International */}
        {isInternational && hasItemsInAnyParcel && (
          <div style={{ marginTop: 20 }}>
            <Divider style={{ margin: '0 0 20px' }} />
            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16 }}>
              Customs Invoice Preview
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {/* Invoice summary */}
              <div
                style={{
                  padding: 14,
                  borderRadius: 8,
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: 'var(--ok)' }}>
                  Invoice Summary
                </div>
                {[
                  ['Total Line Items', String(totalItemsCount)],
                  ['Total Quantity', String(totalQuantity)],
                  ['Total Weight', `${totalWeight.toFixed(2)} kg`],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '4px 0',
                      fontSize: 13,
                    }}
                  >
                    <span className="muted">{label}</span>
                    <Badge tone="default">{value}</Badge>
                  </div>
                ))}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>Total Value</span>
                  <span className="mono" style={{ fontWeight: 600, color: 'var(--ok)' }}>
                    {symbol}{totalValue.toFixed(2)} {currency}
                  </span>
                </div>
              </div>

              {/* Customs readiness */}
              <div
                style={{
                  padding: 14,
                  borderRadius: 8,
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: 'var(--blue)' }}>
                  Customs Readiness
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                  <span className="muted">Completion</span>
                  <span style={{ fontWeight: 600 }}>
                    {completeItems}/{totalItemsCount} ({completionRate.toFixed(0)}%)
                  </span>
                </div>
                <div
                  style={{
                    height: 6,
                    borderRadius: 3,
                    background: 'var(--border)',
                    overflow: 'hidden',
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      width: `${completionRate}%`,
                      background:
                        completionRate === 100
                          ? 'var(--ok)'
                          : completionRate > 50
                          ? 'var(--warn)'
                          : 'var(--err)',
                      transition: 'width 0.3s',
                    }}
                  />
                </div>
                <Callout tone={completionRate === 100 ? 'ok' : 'warn'}>
                  <Icon name={completionRate === 100 ? 'checkCircle' : 'alertCircle'} size={13} />
                  <span style={{ fontSize: 12 }}>
                    {completionRate === 100
                      ? 'All items ready for customs!'
                      : 'Complete missing fields for smooth customs processing'}
                  </span>
                </Callout>
              </div>
            </div>
          </div>
        )}
      </Card>

      <ShipStepNavigation
        onNext={goToNextStep}
        nextLabel="Continue to Summary"
        nextDisabled={!stepValidation[ShippingSteps.PACKAGE_CONTENTS]}
        validationMessage={
          !stepValidation[ShippingSteps.PACKAGE_CONTENTS]
            ? 'Please complete all item details and ensure weight limits are not exceeded'
            : undefined
        }
      />
    </div>
  );
};
